import { useSyncExternalStore } from "react";
import api, { BACKEND } from "@/lib/api";
import { toast } from "sonner";

const IMAGE_EXT = ["jpg", "jpeg", "png", "webp", "gif", "svg", "bmp", "avif", "heic", "heif", "tif", "tiff", "ico"];
const VIDEO_EXT = ["mp4", "webm", "mov", "ogg", "ogv", "m4v", "avi", "mkv", "wmv", "mpg", "mpeg", "3gp"];
const AUDIO_EXT = ["mp3", "wav", "m4a", "aac", "flac", "oga", "wma", "aiff", "opus"];

export const LARGE_FILE_MB = 50;

export const extOf = (name = "") => (name.includes(".") ? name.split(".").pop().toLowerCase() : "");

export function kindOfFile(file) {
  const t = file?.type || "";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t.startsWith("audio/")) return "audio";
  const ext = extOf(file?.name);
  if (IMAGE_EXT.includes(ext)) return "image";
  if (VIDEO_EXT.includes(ext)) return "video";
  if (AUDIO_EXT.includes(ext)) return "audio";
  return "file";
}

export function downloadUrl(url = "") {
  if ((url.includes("/api/media/") || url.includes("/api/documents/")) && !url.includes("?")) return `${url}?download=1`;
  return url;
}

export const OFFICE_EXT = ["doc", "docx", "odt", "rtf", "xls", "xlsx", "ods", "csv", "ppt", "pptx", "odp"];

// URL del PDF di anteprima generato dal server per i file Office (null se il file non è previewabile)
export function previewPdfUrl(url = "") {
  const clean = url.split("?")[0];
  let m = clean.match(/^(.*\/api\/(?:media|image)\/[^/]+)$/);
  if (m) return `${m[1]}/preview.pdf`;
  m = clean.match(/^(.*\/api\/documents\/[^/]+)\/file$/);
  if (m) return `${m[1]}/preview.pdf`;
  return null;
}

export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Store globale degli upload in corso (barra di avanzamento) ---
let uploads = [];
const listeners = new Set();
const emit = () => { uploads = [...uploads]; listeners.forEach((l) => l()); };
const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };
export const useUploads = () => useSyncExternalStore(subscribe, () => uploads, () => uploads);

function trackUpload(file) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  uploads.push({ id, name: file.name, size: file.size, loaded: 0, percent: 0, status: "uploading" });
  emit();
  return {
    progress(e) {
      const u = uploads.find((x) => x.id === id);
      if (!u) return;
      u.loaded = e.loaded; u.percent = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
      if (u.percent >= 100) u.status = "processing";
      emit();
    },
    done(ok) {
      const u = uploads.find((x) => x.id === id);
      if (u) { u.status = ok ? "done" : "error"; u.percent = ok ? 100 : u.percent; emit(); }
      setTimeout(() => { uploads = uploads.filter((x) => x.id !== id); emit(); }, ok ? 2500 : 6000);
    },
  };
}

export function dismissUpload(id) { uploads = uploads.filter((x) => x.id !== id); emit(); }

// Carica qualsiasi file con barra di avanzamento; avvisa (senza bloccare) se il file è grande.
export async function uploadAnyFile(file, onProgress) {
  if (file.size > LARGE_FILE_MB * 1024 * 1024) {
    toast.warning(`File grande (${formatSize(file.size)}): il caricamento potrebbe richiedere diversi minuti. Lascia la pagina aperta.`, { duration: 8000 });
  }
  const kind = kindOfFile(file);
  const fd = new FormData();
  fd.append("file", file);
  const endpoint = kind === "image" ? "/upload/image" : "/media/upload";
  const tracker = trackUpload(file);
  try {
    const { data } = await api.post(endpoint, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 0,
      onUploadProgress: (e) => { tracker.progress(e); onProgress?.(e); },
    });
    tracker.done(true);
    return { url: `${BACKEND}${data.url}`, relUrl: data.url, kind, name: file.name, size: file.size, ext: extOf(file.name) };
  } catch (e) {
    tracker.done(false);
    throw e;
  }
}

// Variante generica: POST multipart verso un endpoint custom con campi aggiuntivi (es. documenti)
export async function uploadWithProgress(endpoint, file, fields = {}) {
  if (file.size > LARGE_FILE_MB * 1024 * 1024) {
    toast.warning(`File grande (${formatSize(file.size)}): il caricamento potrebbe richiedere diversi minuti. Lascia la pagina aperta.`, { duration: 8000 });
  }
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, v ?? ""));
  const tracker = trackUpload(file);
  try {
    const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0, onUploadProgress: tracker.progress });
    tracker.done(true);
    return data;
  } catch (e) {
    tracker.done(false);
    throw e;
  }
}
